// blockReport — 유튜브 차단 신고·감시 체계(B+C 하이브리드, 대표 결정 2026-09-14). 순수 헬퍼 + 시안 스위치.
//   기획 docs/ops/youtube-blocked-report-plan.md · 시안 docs/design/youtube-blocked-hybrid-variants.html
//   표 inspection_block_report 한 행 = 신고 한 건. status: suspect(차단 의심, 시스템) → reported(차단 신고, 크리에이터) → resolved(권리사 확인) / dismissed(해당 없음)
//   kind: copyright_block(저작권 침해 신고로 차단) · claim(수익 창출 클레임) · unknown(의심에 「네」만)
//   화면은 검수 행에 `block_report`(열린 신고 1개 또는 null)를 붙여 쓴다 — attachBlockReports(). inspectionProgress 가 이 값을 읽어 「공개」 칸 색을 바꾼다.
//   화면 배치(2026-09-14 권고안으로 진행): 크리에이터는 V1(검수 목록 행 안에 버튼·답·배지), 권리사는 V3(검수 첫 화면 「오늘 할 일」 카드 + 큐 칩·띠·필터). 시안 V2 와 스위치는 제거했다.

import { tt } from "../i18n/tt";
import { parseYoutubeVideoId } from "./promotionCalculations";

export const BLOCK_OPEN_STATUSES = ["suspect", "reported"];

export function isOpenBlock(r) {
  return !!r && BLOCK_OPEN_STATUSES.includes(r.status);
}

export function blockKindLabel(kind) {
  if (kind === "copyright_block") return tt("a.blk.kind.copyright", "저작권 차단");
  if (kind === "claim") return tt("a.blk.kind.claim", "클레임");
  return tt("a.blk.kind.unknown", "차단");
}

export function blockStatusLabel(status) {
  if (status === "suspect") return tt("a.blk.st.suspect", "차단 의심");
  if (status === "reported") return tt("a.blk.st.reported", "차단 신고");
  if (status === "resolved") return tt("a.blk.st.resolved", "확인 완료");
  if (status === "dismissed") return tt("a.blk.st.dismissed", "해당 없음");
  return String(status || "");
}

/** 「9/14 04:00」 — 막대 「지금」 줄·칩에 쓰는 짧은 날짜 */
export function fmtBlockDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * 검수 행 배열에 열린 신고(1건)와 이력을 붙인다. 같은 검수 건에 열린 신고가 둘이면 최신 것.
 * @returns rows 와 같은 순서의 새 배열. 각 행: { ...row, block_report: open|null, block_history: report[] }
 */
export function attachBlockReports(rows = [], reports = []) {
  const byInsp = new Map();
  (reports || []).forEach((r) => {
    if (isWorkLevelReport(r)) return; // 작품 단위 신고(2026-09-23)는 검수 행에 붙지 않는다
    const k = String(r.inspection_id);
    if (!byInsp.has(k)) byInsp.set(k, []);
    byInsp.get(k).push(r);
  });
  const ts = (r) => new Date(r.reported_at || r.detected_at || r.created_at || 0).getTime();
  return (rows || []).map((row) => {
    const list = (byInsp.get(String(row.id)) || []).slice().sort((a, b) => ts(b) - ts(a));
    const open = list.find(isOpenBlock) || null;
    return { ...row, block_report: open, block_history: list };
  });
}

/** 열린 신고만 (권리사 띠·필터·할 일 카드용) */
export function openReports(reports = []) {
  return (reports || []).filter(isOpenBlock);
}

// ── 작품 단위 차단 알림 (2026-09-23) ─────────────────────────────────────────────────────────
//   검수 없이 올린 영상도 알릴 수 있게 inspection_id 가 빈 신고 행(「작품 단위」)이 생겼다. 새 열은 없다 — 출처는 inspection_id 유무로 가른다.
//   마이그레이션 20260923230000_block_report_work_level.sql · 인수인계 docs/ops/youtube-blocked-report-handover.md 2026-09-23 절.

/** 작품 단위 신고인가(검수 건 없이 채널 + 작품으로 알린 것) */
export function isWorkLevelReport(r) {
  return !!r && (r.inspection_id === null || r.inspection_id === undefined || r.inspection_id === "");
}

// 작품 단위 신고의 creator_note 는 「언제부터」와 「메모」를 한 칸에 담는다(RPC 인자가 p_note 하나).
//   DB 에 저장되는 표식이라 번역하지 않는다 — 권리사 알림 본문에도 이 모양 그대로 들어간다.
export const WORK_NOTE_SINCE = "언제부터: ";
export const WORK_NOTE_MEMO = "메모: ";

export function composeWorkNote({ since, memo } = {}) {
  const lines = [];
  const s = String(since || "").trim();
  const m = String(memo || "").trim();
  if (s) lines.push(WORK_NOTE_SINCE + s);
  if (m) lines.push(WORK_NOTE_MEMO + m);
  return lines.length ? lines.join("\n") : null;
}

/** 신고 한 건의 {since, memo}. 검수 건 단위 신고는 시트 입력칸이 「언제부터」 하나라 creator_note 전체가 since 다. */
export function splitReportNote(r) {
  const note = String(r?.creator_note || "").trim();
  if (!note) return { since: "", memo: "" };
  if (!isWorkLevelReport(r)) return { since: note, memo: "" };
  let since = "";
  const memo = [];
  note.split("\n").forEach((line) => {
    if (line.startsWith(WORK_NOTE_SINCE)) since = line.slice(WORK_NOTE_SINCE.length).trim();
    else if (line.startsWith(WORK_NOTE_MEMO)) memo.push(line.slice(WORK_NOTE_MEMO.length).trim());
    else if (line.trim()) memo.push(line.trim());
  });
  return { since, memo: memo.join(" ") };
}

/** 작품 고르기에서 막는 작품 — 검수 필수(required)인데 이 채널에 승인된 검수 건이 없다. 서버 block_report_create_work 와 같은 규칙 */
export function workPickDisabled(work, completedWorkIds) {
  if (!work || work.inspection_policy !== "required") return false;
  const set = completedWorkIds instanceof Set ? completedWorkIds : new Set(completedWorkIds || []);
  return !set.has(String(work.id));
}

/**
 * 작품 고르기 목록 정렬 — 이 채널에 최근 올린 영상이 있는 작품이 먼저(최근 업로드 순), 나머지는 제목순.
 * @param works licensed_video 행 [{id, title, inspection_policy, …}]
 * @param lastUploadByWork Map|object — 작품 id → 이 채널 최근 업로드 시각(youtube_video_map.published_at 중 최댓값)
 * @param completedWorkIds Set|array — 이 채널에서 검수 승인(completed)된 작품 id
 * @returns [{...work, lastUploadAt: string|null, disabled: boolean}]
 */
export function sortWorkPickerItems(works = [], lastUploadByWork = new Map(), completedWorkIds = new Set()) {
  const get = (id) => (lastUploadByWork instanceof Map ? lastUploadByWork.get(String(id)) : (lastUploadByWork || {})[String(id)]) || null;
  const ts = (v) => { const t = v ? new Date(v).getTime() : NaN; return Number.isNaN(t) ? null : t; };
  const completed = completedWorkIds instanceof Set ? completedWorkIds : new Set((completedWorkIds || []).map(String));
  return (works || [])
    .map((w) => ({ ...w, lastUploadAt: get(w.id), disabled: workPickDisabled(w, completed) }))
    .sort((a, b) => {
      const ta = ts(a.lastUploadAt);
      const tb = ts(b.lastUploadAt);
      if (ta != null && tb != null && ta !== tb) return tb - ta;
      if (ta != null && tb == null) return -1;
      if (ta == null && tb != null) return 1;
      return String(a.title || "").localeCompare(String(b.title || ""), "ko");
    });
}

/** 작품 id → 이 채널 최근 업로드 시각. rows = youtube_video_map [{licensed_video_id, published_at}] */
export function lastUploadMap(rows = []) {
  const m = new Map();
  (rows || []).forEach((r) => {
    const id = r?.licensed_video_id;
    if (!id || !r.published_at) return;
    const k = String(id);
    const prev = m.get(k);
    if (!prev || new Date(r.published_at).getTime() > new Date(prev).getTime()) m.set(k, r.published_at);
  });
  return m;
}

/** 작품 id → 열린 신고(의심·신고)가 있는 채널 수. 권리사 작품 목록·상세의 빨간 숫자 */
export function openChannelCountsByWork(reports = []) {
  const byWork = new Map();
  (reports || []).forEach((r) => {
    if (!isOpenBlock(r) || !r.video_id || !r.channel_id) return;
    const k = String(r.video_id);
    if (!byWork.has(k)) byWork.set(k, new Set());
    byWork.get(k).add(String(r.channel_id));
  });
  const out = new Map();
  byWork.forEach((set, k) => out.set(k, set.size));
  return out;
}

/**
 * 한 작품의 신고를 채널별로 묶는다(권리사 [차단 알림] 창). 검수 건 단위 + 작품 단위를 한 줄로 합친다.
 *   open: 열린 신고가 하나라도 있으면 「확인 필요」, 아니면 「처리 완료」.
 *   earliestSince: 가장 먼저 알린 신고의 「언제부터」(자유 입력이라 비교할 수 없어 알린 시각 기준) · latestAt: 가장 최근 알린 시각
 *   videoLinks: 검수 건 단위 신고의 영상 주소(작품 단위는 주소가 없다)
 */
export function groupReportsByChannel(reports = []) {
  const at = (r) => r.reported_at || r.detected_at || r.created_at || null;
  const t = (r) => { const v = at(r) ? new Date(at(r)).getTime() : NaN; return Number.isNaN(v) ? 0 : v; };
  const groups = new Map();
  (reports || []).forEach((r) => {
    if (!r || !r.channel_id) return;
    const k = String(r.channel_id);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  });
  const out = [];
  groups.forEach((list, channelId) => {
    const byOld = list.slice().sort((a, b) => t(a) - t(b));
    const openList = list.filter(isOpenBlock);
    const shown = openList.length ? openList : list; // 확인 필요 줄은 열린 신고만으로 그린다
    const shownOld = shown.slice().sort((a, b) => t(a) - t(b));
    const sinceFrom = shownOld.find((r) => splitReportNote(r).since);
    const kinds = [];
    shownOld.forEach((r) => { if (r.kind && !kinds.includes(r.kind)) kinds.push(r.kind); });
    const memos = [];
    shownOld.forEach((r) => { const m = splitReportNote(r).memo; if (m && !memos.includes(m)) memos.push(m); });
    const videoLinks = [];
    shownOld.forEach((r) => { if (!isWorkLevelReport(r) && r.youtube_url && !videoLinks.some((v) => v.url === r.youtube_url)) videoLinks.push({ url: r.youtube_url, episode: r.episode, episode_part: r.episode_part, id: r.id }); });
    // 작품 단위 신고의 막힌 영상 주소(2026-09-23 수정) — 영상 ID 로 중복 제거, 검수 건 링크와 같은 영상이면 빼고 「영상 1 · 영상 2 …」로
    const linkedIds = new Set(videoLinks.map((v) => youtubeIdOf(v.url)).filter(Boolean));
    const workUrls = [];
    const workIds = new Set();
    shownOld.forEach((r) => {
      if (!isWorkLevelReport(r)) return;
      const list = Array.isArray(r.youtube_urls) && r.youtube_urls.length ? r.youtube_urls : (r.youtube_url ? [r.youtube_url] : []);
      list.forEach((u) => {
        const id = youtubeIdOf(u) || u;
        if (!u || workIds.has(id) || linkedIds.has(id)) return;
        workIds.add(id);
        workUrls.push(u);
      });
    });
    // 신고자 확인 절차(2026-11-03) — 크리에이터가 올린 소유권 주장 캡처(비공개 버킷 경로) · 「다른 신고자」라고 답한 신고가 있는지
    const captures = [];
    shownOld.forEach((r) => { (Array.isArray(r.capture_paths) ? r.capture_paths : []).forEach((p) => { if (p && !captures.includes(p)) captures.push(p); }); });
    const claimantOther = shownOld.some((r) => r.claimant_answer === "other");
    const latest = shownOld[shownOld.length - 1] || byOld[byOld.length - 1];
    const resolvedAt = openList.length ? null : byOld.map((r) => r.resolved_at).filter(Boolean).sort().pop() || null;
    out.push({
      channel_id: channelId,
      channel_name: (latest && latest.channel_name) || (byOld.find((r) => r.channel_name) || {}).channel_name || "",
      open: openList.length > 0,
      reports: shownOld,
      all: byOld,
      kinds,
      earliestSince: sinceFrom ? splitReportNote(sinceFrom).since : "",
      firstAt: shownOld.length ? at(shownOld[0]) : null,
      latestAt: latest ? at(latest) : null,
      memos,
      videoLinks,
      workUrls,
      captures,
      claimantOther,
      returnedAsDispute: !openList.length && byOld.some((r) => r.detail && r.detail.returned_as_dispute === true),
      hasWorkLevel: shown.some(isWorkLevelReport),
      hasInspectionLevel: shown.some((r) => !isWorkLevelReport(r)),
      onlySuspect: openList.length > 0 && openList.every((r) => r.status === "suspect"),
      resolvedAt,
    });
  });
  // 확인 필요 먼저, 그 안에서 최근 알림이 위
  const ts = (g) => { const v = g.latestAt ? new Date(g.latestAt).getTime() : 0; return Number.isNaN(v) ? 0 : v; };
  return out.sort((a, b) => (a.open === b.open ? ts(b) - ts(a) : a.open ? -1 : 1));
}

// ── 막힌 영상 주소 (2026-09-23 수정 — 작품마다 1개 이상 필수) ────────────────────────────────
//   권리사는 채널 화이트리스트 말고도 영상별 소유권 주장 해제로 풀 수 있어 정확한 영상 주소가 필요하다.
//   서버 block_report_youtube_id / block_report_create_work 와 같은 규칙: 유튜브 영상 주소(youtu.be · watch?v= · shorts · embed)만,
//   영상 ID 11자, 쇼츠는 /shorts/<id> 그 밖은 watch?v=<id> 로 정규화, 영상 ID 로 중복 제거.
const YT_VIDEO_ID = /^[\w-]{11}$/;

/** 유튜브 영상 주소 → 영상 ID(11자) 또는 null */
export function youtubeIdOf(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  const id = parseYoutubeVideoId(raw);
  return id && YT_VIDEO_ID.test(id) ? id : null;
}

/** 주소 하나를 정규화 — { id, url } 또는 null(유튜브 영상 주소가 아님) */
export function normalizeBlockUrl(url) {
  const id = youtubeIdOf(url);
  if (!id) return null;
  const isShorts = /^https?:\/\/(www\.|m\.)?youtube\.com\/shorts\//i.test(String(url).trim());
  return { id, url: isShorts ? `https://www.youtube.com/shorts/${id}` : `https://www.youtube.com/watch?v=${id}` };
}

/**
 * 한 작품에 적은 주소 칸들을 검사한다.
 * @param entries 입력 문자열 배열(빈 칸 포함)
 * @param opts.channelYtId 고른 채널의 유튜브 채널 ID(channel.channel_id, UC…)
 * @param opts.mapChannelByVideoId Map|object — 영상 ID → youtube_video_map.channel_id(UC…). 없으면(수집 전) 통과
 * @returns [{ raw, id, url, error: null|"empty"|"invalid"|"other_channel", duplicate: bool }]
 */
export function checkWorkUrls(entries = [], { channelYtId = null, mapChannelByVideoId = new Map() } = {}) {
  const seen = new Set();
  const mapGet = (id) => (mapChannelByVideoId instanceof Map ? mapChannelByVideoId.get(id) : (mapChannelByVideoId || {})[id]);
  return (entries || []).map((raw) => {
    const text = String(raw || "").trim();
    if (!text) return { raw, id: null, url: null, error: "empty", duplicate: false };
    const n = normalizeBlockUrl(text);
    if (!n) return { raw, id: null, url: null, error: "invalid", duplicate: false };
    const owner = mapGet(n.id);
    const error = owner && channelYtId && owner !== channelYtId ? "other_channel" : null;
    const duplicate = seen.has(n.id);
    seen.add(n.id);
    return { raw, id: n.id, url: n.url, error, duplicate };
  });
}

/** 이 작품은 보낼 수 있나 — 올바른 주소 1개 이상 + 적힌 칸은 전부 올바름(형식·채널). 빈 칸은 무시 */
export function workUrlsOk(checked = []) {
  const filled = (checked || []).filter((c) => c.error !== "empty");
  return filled.length > 0 && filled.every((c) => !c.error);
}

/** 고른 작품 전부가 보낼 수 있나 */
export function allWorkUrlsOk(pickedIds = [], checkedByWork = {}) {
  const ids = [...(pickedIds || [])];
  return ids.length > 0 && ids.every((id) => workUrlsOk(checkedByWork[String(id)] || []));
}

/** RPC block_report_create_work 의 p_items — [{ video_id, urls: [정규화·중복 제거] }] */
export function buildWorkItems(pickedIds = [], urlsByWork = {}) {
  return [...(pickedIds || [])].map((id) => {
    const urls = [];
    const ids = new Set();
    (urlsByWork[String(id)] || []).forEach((raw) => {
      const n = normalizeBlockUrl(raw);
      if (!n || ids.has(n.id)) return;
      ids.add(n.id);
      urls.push(n.url);
    });
    return { video_id: String(id), urls };
  });
}

/** 검수 행 「…」에서 열 때 미리 채울 값 — 그 행의 채널·작품·영상 주소 */
export function presetFromInspection(item) {
  if (!item) return null;
  return {
    channelId: item.channel_id != null ? String(item.channel_id) : null,
    videoId: item.video_id != null ? String(item.video_id) : null,
    url: item.published_youtube_url || item.youtube_url || "",
  };
}

/** 작품 단위 신고 결과(skipped[].reason) 사람 말 */
export function workSkipReasonLabel(reason) {
  if (reason === "inspection_required") return tt("a.blk.work.skip.required", "검수 필수 작품이라 검수 신청이 필요해요");
  if (reason === "already_open") return tt("a.blk.work.skip.open", "이미 알림을 보냈고 권리사 확인을 기다리는 중이에요");
  if (reason === "not_linked") return tt("a.blk.work.skip.linked", "이 채널로 신청한 작품이 아니에요");
  if (reason === "not_found") return tt("a.blk.work.skip.notfound", "작품을 찾지 못했어요");
  if (reason === "url_required") return tt("a.blk.work.skip.url", "막힌 영상 주소가 없어요");
  // 신고자 확인 절차(2026-11-03 · blockClaimant.js)
  if (reason === "claimant_dispute") return tt("a.blk.work.skip.dispute", "유튜브에서 이의제기로 풀 수 있는 신고예요");
  if (reason === "claimant_required") return tt("a.blk.work.skip.claimant", "신고자 확인이 필요해요 — 창을 닫고 다시 알려 주세요");
  if (reason === "capture_required") return tt("a.blk.work.skip.capture", "소유권 주장 화면 캡처가 필요해요");
  return String(reason || "");
}
